import { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Menu,
  X,
  Cog,
  Cpu,
  PackageCheck,
  MoveHorizontal,
  Boxes,
  Bot,
  Factory,
  ArrowRight,
} from "lucide-react";

import InquiryDialog from "./InquiryDialog";
import { NAV } from "../constants/testIds/site";

const CAT_ICONS = {
  Cpu,
  PackageCheck,
  MoveHorizontal,
  Boxes,
  Bot,
  Factory,
  Cog,
};

// Base link style — the active/hover underline is handled by a sibling span
// so we can animate it in/out with a scale transform instead of toggling color only.
const linkBase =
  "group relative inline-flex items-center rounded-full px-3 py-2 text-[14px] font-semibold tracking-[0.01em] text-slate-600 transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/70 hover:text-brand-navy";

const active = "bg-brand-navy text-white shadow-[0_10px_24px_-14px_rgba(10,37,64,0.9)]";

const NAV_ITEMS = [
  { to: "/", label: "Home", testId: NAV.home, end: true },
  { to: "/about", label: "About", testId: NAV.about },
  { to: "/products", label: "Products", testId: NAV.products },
  { to: "/industries", label: "Industries", testId: NAV.industries },
  { to: "/contact", label: "Contact", testId: NAV.contact },
  { to: "/admin", label: "Admin", testId: NAV.contact },
];

export default function Navbar({ categories = [] }) {
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [inqOpen, setInqOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Small delay before closing the mega menu so moving the cursor from the
  // trigger down into the panel doesn't cause it to flicker shut.
  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const scheduleCloseMega = () => {
    closeTimer.current = setTimeout(() => setMegaOpen(false), 120);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b transition-all duration-300 ${
          scrolled
            ? "border-slate-200/70 bg-white/80 shadow-[0_20px_45px_-25px_rgba(10,37,64,0.55)] backdrop-blur-xl"
            : "border-slate-100/80 bg-white/65 backdrop-blur-lg"
        }`}
      >
        <div
          className={`section-x mx-auto flex max-w-[1440px] items-center justify-between transition-all duration-300 ${
            scrolled ? "h-[66px]" : "h-[80px]"
          }`}
        >
          <Link
            to="/"
            data-testid={NAV.logo}
            className="group relative flex items-center rounded-xl px-1 py-1"
          >
            <span className="pointer-events-none absolute inset-0 -z-10 rounded-xl bg-gradient-to-r from-brand-navy/10 via-transparent to-accent/20 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100" />
           <img
             src="/Logonew.png"
             alt="Buildwit Technology"
             className="h-auto w-[150px] max-w-[45vw] object-contain transition-transform duration-300 group-hover:scale-[1.03] sm:w-[176px]"
            />
          </Link>

          <nav className="hidden items-center gap-2 lg:flex">
            <NavLink
              to="/"
              end
              data-testid={NAV.home}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  Home
                  <span
                    className={`pointer-events-none absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current transition-all duration-300 ease-out ${
                      isActive
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    }`}
                  />
                </>
              )}
            </NavLink>

            <NavLink
              to="/about"
              data-testid={NAV.about}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  About
                  <span
                    className={`pointer-events-none absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current transition-all duration-300 ease-out ${
                      isActive
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    }`}
                  />
                </>
              )}
            </NavLink>

            <div
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={scheduleCloseMega}
            >
              <NavLink
                to="/products"
                data-testid={NAV.products}
                className={({ isActive }) =>
                  `${linkBase} flex items-center gap-1 ${
                    isActive || megaOpen ? active : ""
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    Products
                    <svg
                      className={`h-3.5 w-3.5 transition-transform duration-200 ${
                        megaOpen ? "-rotate-180" : ""
                      }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span
                      className={`pointer-events-none absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current transition-all duration-300 ease-out ${
                        isActive || megaOpen
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>

              <div
                className={`absolute left-1/2 top-full -translate-x-1/2 pt-3 transition-all duration-200 ease-out ${
                  megaOpen
                    ? "pointer-events-auto translate-y-0 opacity-100"
                    : "pointer-events-none -translate-y-1.5 opacity-0"
                }`}
              >
                <div className="grid w-[min(92vw,760px)] grid-cols-2 gap-2 rounded-2xl border border-slate-200/90 bg-white/95 p-5 shadow-[0_30px_80px_-20px_rgba(10,37,64,0.32)] backdrop-blur-xl">
                  <div className="col-span-2 mb-2 flex items-center justify-between border-b border-slate-100 px-2 pb-3">
                    <span className="overline text-slate-500">
                      Machinery Portfolio
                    </span>

                    <Link
                      to="/products"
                      className="group/link inline-flex items-center gap-1 text-xs font-semibold text-accent"
                    >
                      View all
                      <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover/link:translate-x-0.5" />
                    </Link>
                  </div>

                  {categories.map((c) => {
                    const Ico = CAT_ICONS[c.icon] || Cog;

                    return (
                      <Link
                        key={c.slug}
                        to={`/products?category=${c.slug}`}
                        onClick={() => setMegaOpen(false)}
                        className="group/item flex items-start gap-3 rounded-xl border border-transparent p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-200 hover:bg-gradient-to-r hover:from-slate-50 hover:to-white"
                      >
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-navy/5 text-brand-navy transition-all duration-200 group-hover/item:-translate-y-0.5 group-hover/item:bg-accent group-hover/item:text-white">
                          <Ico className="h-4 w-4" />
                        </span>

                        <span className="flex flex-col">
                          <span className="text-sm font-semibold leading-tight text-brand-navy">
                            {c.name}
                          </span>

                          <span className="mt-0.5 text-xs leading-snug text-slate-500">
                            {c.description}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <NavLink
              to="/industries"
              data-testid={NAV.industries}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  Industries
                  <span
                    className={`pointer-events-none absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current transition-all duration-300 ease-out ${
                      isActive
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    }`}
                  />
                </>
              )}
            </NavLink>

            <NavLink
              to="/contact"
              data-testid={NAV.contact}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? active : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  Contact
                  <span
                    className={`pointer-events-none absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current transition-all duration-300 ease-out ${
                      isActive
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    }`}
                  />
                </>
              )}
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid={NAV.quoteBtn}
              onClick={() => setInqOpen(true)}
              className="group hidden h-10 items-center gap-2 rounded-full border border-accent/20 bg-accent px-5 text-sm font-semibold text-white shadow-[0_12px_28px_-16px_rgba(249,115,22,0.8)] transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-[0_18px_36px_-16px_rgba(249,115,22,0.9)] active:translate-y-0 sm:inline-flex"
            >
              Request Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>

            <button
              data-testid={NAV.mobileToggle}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-brand-navy shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-white lg:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              <Menu
                className={`absolute h-5 w-5 transition-all duration-200 ${
                  open ? "rotate-90 opacity-0" : "rotate-0 opacity-100"
                }`}
              />
              <X
                className={`absolute h-5 w-5 transition-all duration-200 ${
                  open ? "rotate-0 opacity-100" : "-rotate-90 opacity-0"
                }`}
              />
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden border-t border-slate-200/80 bg-white/95 shadow-[0_20px_45px_-30px_rgba(10,37,64,0.45)] backdrop-blur-lg transition-[grid-template-rows] duration-300 ease-out lg:hidden ${
            open ? "grid grid-rows-[1fr]" : "grid grid-rows-[0fr] border-t-0"
          }`}
        >
          <div className="min-h-0">
            <div className="section-x flex flex-col gap-2 py-4">
              {NAV_ITEMS.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  style={{ transitionDelay: open ? `${i * 40}ms` : "0ms" }}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-xl border px-3 py-3 text-sm font-semibold transition-all duration-300 ${
                      open
                        ? "translate-y-0 opacity-100"
                        : "translate-y-1 opacity-0"
                    } ${
                      isActive
                        ? "border-brand-navy bg-brand-navy text-white"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}

              <button
                onClick={() => {
                  setOpen(false);
                  setInqOpen(true);
                }}
                style={{
                  transitionDelay: open ? `${NAV_ITEMS.length * 40}ms` : "0ms",
                }}
                className={`mt-2 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent text-sm font-semibold text-white shadow-[0_12px_28px_-16px_rgba(249,115,22,0.8)] transition-all duration-300 hover:bg-accent-hover ${
                  open ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"
                }`}
              >
                Request Quote
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <InquiryDialog open={inqOpen} onOpenChange={setInqOpen} />
    </>
  );
}
